import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus, UserMinus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  REQUISITION_PRIORITIES,
  REQUISITION_PRIORITY_LABELS,
  REQUISITION_STATUSES,
  REQUISITION_STATUS_LABELS,
  SLA_STATES,
  SLA_STATE_LABELS,
  formatMoney,
  type ApprovalQueueItem,
  type RequisitionRow,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { DataTable } from '../../../components/data-table/DataTable';
import { TableToolbar } from '../../../components/data-table/TableToolbar';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { MultiSelect, Select } from '../../../components/Select';
import {
  EmptyState,
  FilteredEmptyState,
  ForbiddenState,
} from '../../../components/system-states/SystemState';
import { formatDate } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { isCurrency } from '../requisition-form-values';
import { EMPTY_SELECTION, type SelectionState } from '../../../lib/selection';
import { useCanEdit, useCanView, useCurrentSession } from '../../../stores/session';
import { useDepartments, useVentures } from '../../organisation/api/organisation-api';
import { useApprovalQueue, useRequisitions } from '../api/requisitions-api';
import { BoardDecision } from '../components/BoardDecision';
import { RecordExitDialog } from '../components/RecordExitDialog';
import { RequisitionStatusChip, SlaFigure, requisitionTone } from '../components/RequisitionBadges';
import { slaFigures } from '../sla-format';
import styles from './RequisitionBoardPage.module.css';
import fit from '../../../components/FitPage.module.css';

/**
 * REQ-02 — the requisition board: every requisition the viewer may see, by status.
 *
 * Workflow review 2026-10-29: approvals live here now — the Approvals page is gone. The
 * "Waiting on me" view (open by default when something waits) puts Approve, Send back and
 * Reject on the row, and a track shows where each request is: Requested → HR check →
 * Leadership approval → Job created.
 *
 * A table, not columns of cards (product owner, 2026-09-28): it holds more, sorts,
 * and every filter the master plan names — status, venture, department, priority,
 * SLA state — lives in the URL, so a filtered board is a link (06-qol §6).
 */

const PAGE_SIZE = 200;

/** The board's quick views (workflow review 2026-10-29). */
const VIEWS = ['mine', 'hr', 'leadership', 'approved', 'returned', 'all'] as const;
type BoardView = (typeof VIEWS)[number];
const VIEW_LABELS: Readonly<Record<BoardView, string>> = {
  mine: 'Waiting on me',
  hr: 'With HR',
  leadership: 'With Leadership',
  approved: 'Approved, job made',
  returned: 'Sent back or rejected',
  all: 'All',
};

export interface RequisitionBoardSearch {
  readonly view?: string;
  readonly q?: string;
  readonly status?: string;
  readonly priority?: string;
  readonly sla?: string;
  readonly venture?: string;
  readonly department?: string;
}

export function RequisitionBoardPage(): ReactNode {
  const canView = useCanView('requisition');
  const canCreate = useCanEdit('requisition');
  const navigate = useNavigate();
  const search = useSearch({ from: '/app-layout/requisitions' }) as RequisitionBoardSearch;
  const ventures = useVentures();
  const departments = useDepartments();

  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [isRecordingExit, setIsRecordingExit] = useState(false);
  // The minimal exit form is the HR Lead's (master plan S9); the server checks again.
  const isHrLead = useCurrentSession()?.roles.includes('hr_lead') === true;

  const query = search.q ?? '';
  const statuses = listOf(search.status, REQUISITION_STATUSES);
  const priorities = listOf(search.priority, REQUISITION_PRIORITIES);
  // Rule 13 (bug B9): with Time limits off there is no Deadline column, filter or tone, and a deadline filter left in the address is
  // ignored rather than quietly narrowing the list.
  const isSlaOn = useModule('sla');
  const slaStates = isSlaOn ? listOf(search.sla, SLA_STATES) : [];

  function setSearch(next: RequisitionBoardSearch): void {
    void navigate({ to: '/requisitions', search: next, replace: true });
  }

  const queue = useApprovalQueue();
  const [pending, setPending] = useState<Readonly<Record<string, PendingState>>>({});
  const requisitions = useRequisitions({
    page: 1,
    pageSize: PAGE_SIZE,
    ...(query.trim().length >= 2 ? { query: query.trim() } : {}),
    ...(statuses.length > 0 ? { status: statuses.join(',') } : {}),
    ...(priorities.length > 0 ? { priority: priorities.join(',') } : {}),
    ...(slaStates.length > 0 ? { slaState: slaStates.join(',') } : {}),
    ...(search.venture ? { ventureId: search.venture } : {}),
    ...(search.department ? { departmentId: search.department } : {}),
  });

  if (!canView) return <ForbiddenState resource="requisitions" contact="Your admin" />;

  const allRows = requisitions.data?.rows ?? [];
  const waitingOnMe = new Map<string, ApprovalQueueItem>((queue.data ?? []).map((item) => [item.requisitionId, item]));
  const hasOtherFilters =
    statuses.length + priorities.length + slaStates.length > 0 || Boolean(search.venture) || Boolean(search.department) || query !== '';
  // Open on "Waiting on me" whenever something waits and no other view or filter was asked for.
  const view: BoardView = isView(search.view) ? search.view : waitingOnMe.size > 0 && !hasOtherFilters ? 'mine' : 'all';
  const counts = Object.fromEntries(VIEWS.map((v) => [v, allRows.filter((row) => inView(row, v, waitingOnMe)).length])) as Record<BoardView, number>;
  const rows = allRows.filter((row) => inView(row, view, waitingOnMe) || pending[row.id] !== undefined);
  const total = rows.length;
  const ventureName = ventures.data?.find((v) => v.id === search.venture)?.name;
  const departmentName = departments.data?.find((d) => d.id === search.department)?.name;

  const filters = [
    chip('status', statuses.map((s) => REQUISITION_STATUS_LABELS[s]), 'Status', () =>
      setSearch({ ...search, status: undefined }),
    ),
    chip('priority', priorities.map((p) => REQUISITION_PRIORITY_LABELS[p]), 'Priority', () =>
      setSearch({ ...search, priority: undefined }),
    ),
    chip('sla', slaStates.map((s) => SLA_STATE_LABELS[s]), 'Deadline', () =>
      setSearch({ ...search, sla: undefined }),
    ),
    chip('venture', ventureName ? [ventureName] : [], 'Venture', () =>
      setSearch({ ...search, venture: undefined, department: undefined }),
    ),
    chip('department', departmentName ? [departmentName] : [], 'Department', () =>
      setSearch({ ...search, department: undefined }),
    ),
  ].filter((filter) => filter !== null);

  const allColumns: readonly Column<RequisitionRow>[] = [
    {
      id: 'request',
      header: 'Request',
      isIdentity: true,
      defaultWidth: 270,
      cell: (row) => (
        <span className={styles.stack}>
          <Link to="/requisitions/$requisitionId" params={{ requisitionId: row.id }} className={styles.title}>
            {row.title || 'Untitled draft'}
          </Link>
          <span className={styles.meta}>
            {row.reference} · {row.ventureName} · {row.slotsRequested} {row.slotsRequested === 1 ? 'person' : 'people'}
          </span>
          <span className={styles.badges}>
            {row.priority === 'urgent' && <span className={styles.badgeUrgent}>Urgent</span>}
            {row.hireKind === 'replacement' && (
              <span className={styles.badge}>{row.replacesName ? `Replaces ${row.replacesName}` : 'Replacement'}</span>
            )}
            {row.hireKind === 'additional' && <span className={styles.badgeInfo}>Additional</span>}
            {row.positionId === null && <span className={styles.badgeInfo}>New position</span>}
          </span>
        </span>
      ),
      exportValue: (row) => `${row.reference} · ${row.title || 'Untitled draft'}`,
    },
    { id: 'requestedBy', header: 'Asked by', defaultWidth: 140, cell: (row) => row.requestedByName },
    {
      id: 'start',
      header: 'Start',
      defaultWidth: 130,
      cell: (row) =>
        rangeOf(row.targetStartDate ? formatDate(row.targetStartDate) : null, row.targetStartLatest ? formatDate(row.targetStartLatest) : null),
    },
    {
      id: 'budget',
      header: 'Budget',
      defaultWidth: 150,
      cell: (row) =>
        rangeOf(
          moneyOf(row.budgetAmountMinor, row.budgetCurrency),
          moneyOf(row.budgetMaxMinor, row.budgetCurrency),
        ),
    },
    {
      id: 'where',
      header: 'Where it is',
      defaultWidth: 260,
      cell: (row) => <Track row={row} pending={pending[row.id]} />,
      exportValue: (row) => trackText(row, pending[row.id]),
    },
    {
      id: 'move',
      header: 'Your move',
      defaultWidth: 270,
      cell: (row) => {
        const state = pending[row.id];
        if (state) return <span className={styles.meta}>{PENDING_LABELS[state]}</span>;
        const item = waitingOnMe.get(row.id);
        if (item) {
          return (
            <BoardDecision
              item={item}
              onPending={(next) =>
                setPending((current) => {
                  const copy = { ...current };
                  if (next === null) delete copy[row.id];
                  else copy[row.id] = next;
                  return copy;
                })
              }
            />
          );
        }
        if (row.status === 'converted') return <span className={styles.done}>Job created</span>;
        return row.status === 'in_approval' ? <span className={styles.meta}>Not yours yet</span> : null;
      },
      exportValue: () => null,
    },
    {
      id: 'status',
      header: 'Status',
      defaultWidth: 140,
      cell: (row) => <RequisitionStatusChip status={row.status} />,
      exportValue: (row) => REQUISITION_STATUS_LABELS[row.status],
    },
    {
      id: 'sla',
      header: 'Deadline',
      defaultWidth: 250,
      cell: (row) => <SlaFigure sla={row.sla} />,
      exportValue: (row) => (row.sla === null ? null : `${SLA_STATE_LABELS[row.sla.state]} — ${slaFigures(row.sla)}`),
    },
    { id: 'recruiter', header: 'Recruiter', defaultWidth: 160, cell: (row) => row.recruiterName },
  ];
  const columns = isSlaOn ? allColumns : allColumns.filter((column) => column.id !== 'sla');

  const ventureDepartments = (departments.data ?? []).filter(
    (d) => search.venture === undefined || d.ventureId === search.venture,
  );

  return (
    <div className={`${styles.page} ${fit.fit}`}>
      <TableToolbar
        title="Requisitions"
        total={total}
        query={query}
        onQueryChange={(next) => setSearch({ ...search, q: next === '' ? undefined : next })}
        filters={filters}
        onClearFilters={() => setSearch({})}
        filterMenu={
          <div className={styles.filterMenu}>
            <MultiSelect
              label="Status"
              placeholder="Any status"
              value={statuses}
              options={REQUISITION_STATUSES.map((value) => ({ value, label: REQUISITION_STATUS_LABELS[value] }))}
              onChange={(next) => setSearch({ ...search, status: joined(next) })}
            />
            <MultiSelect
              label="Priority"
              placeholder="Any priority"
              value={priorities}
              options={REQUISITION_PRIORITIES.map((value) => ({ value, label: REQUISITION_PRIORITY_LABELS[value] }))}
              onChange={(next) => setSearch({ ...search, priority: joined(next) })}
            />
            {isSlaOn && (
            <MultiSelect
              label="Deadline"
              placeholder="Any deadline state"
              value={slaStates}
              options={SLA_STATES.map((value) => ({ value, label: SLA_STATE_LABELS[value] }))}
              onChange={(next) => setSearch({ ...search, sla: joined(next) })}
            />
            )}
            <Select
              label="Venture"
              placeholder="Every venture"
              value={search.venture ?? null}
              options={(ventures.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
              onChange={(value) => setSearch({ ...search, venture: value, department: undefined })}
            />
            <Select
              label="Department"
              placeholder="Every department"
              value={search.department ?? null}
              options={ventureDepartments.map((d) => ({ value: d.id, label: d.name }))}
              onChange={(value) => setSearch({ ...search, department: value })}
            />
          </div>
        }
      />

      <div className={styles.views} role="tablist" aria-label="Board views">
        {VIEWS.filter((v) => v !== 'mine' || counts.mine > 0 || view === 'mine').map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            className={styles.view}
            onClick={() => setSearch({ ...search, view: v })}
          >
            {VIEW_LABELS[v]}
            {v !== 'all' && <span className={styles.count}>{counts[v]}</span>}
          </button>
        ))}
      </div>

      {(canCreate || isHrLead) && (
        <div className={styles.actions}>
          {isHrLead && (
            <Button variant="ghost" icon={UserMinus} onClick={() => setIsRecordingExit(true)}>
              Record a leaver
            </Button>
          )}
          {canCreate && (
            <Button variant="solid" icon={Plus} onClick={() => void navigate({ to: '/requisitions/new' })}>
              Request a hire
            </Button>
          )}
        </div>
      )}

      <DataTable
        screenId="requisition-board"
        label="Requisitions"
        columns={columns}
        rows={rows}
        toneOf={(row) => requisitionTone(row.status, isSlaOn ? row.sla : null)}
        getRowId={(row) => row.id}
        total={total}
        sort={sort}
        onSortChange={setSort}
        selection={selection}
        onSelectionChange={setSelection}
        onOpenRow={(row) =>
          void navigate({ to: '/requisitions/$requisitionId', params: { requisitionId: row.id } })
        }
        linkOf={(row) => `/requisitions/${row.id}`}
        exportName="requisitions"
        views={{ screen: 'requisitions', filters: viewFilters(search), onApply: (next) => setSearch({ ...next }) }}
        isLoading={requisitions.isPending}
        emptyState={
          filters.length > 0 || query !== '' ? (
            <FilteredEmptyState
              filterCount={filters.length + (query === '' ? 0 : 1)}
              onClearFilters={() => setSearch({})}
            />
          ) : (
            <EmptyState
              title="No requisitions yet"
              body="A requisition is a formal request to hire. Raise one and it goes to its approvers; once approved, its time-to-fill clock starts."
              action={
                canCreate ? (
                  <Button variant="solid" icon={Plus} onClick={() => void navigate({ to: '/requisitions/new' })}>
                    Request a hire
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />

      {isRecordingExit && <RecordExitDialog onClose={() => setIsRecordingExit(false)} />}
    </div>
  );
}

/** S32 (Q28): a saved view keeps the address bar's filters exactly as they are written there. */
function viewFilters(search: RequisitionBoardSearch): Record<string, string> {
  const entries: [string, unknown][] = Object.entries(search);
  const filters: Record<string, string> = {};
  for (const [key, value] of entries) {
    if (typeof value === 'string') filters[key] = value;
  }
  return filters;
}

/** The URL's comma list, keeping only values the list knows. */
function listOf<T extends string>(raw: string | undefined, known: readonly T[]): T[] {
  return (raw?.split(',') ?? []).filter((value): value is T => known.some((k) => k === value));
}

function joined(values: readonly string[]): string | undefined {
  return values.length > 0 ? values.join(',') : undefined;
}

function chip(
  id: string,
  labels: readonly string[],
  name: string,
  onRemove: () => void,
): { id: string; label: string; onRemove: () => void } | null {
  if (labels.length === 0) return null;
  return { id, label: `${name}: ${labels.join(', ')}`, onRemove };
}

type PendingState = 'approved' | 'sent_back' | 'rejected';

const PENDING_LABELS: Readonly<Record<PendingState, string>> = {
  approved: 'Approved by you',
  sent_back: 'Sent back',
  rejected: 'Rejected',
};

function isView(value: string | undefined): value is BoardView {
  return VIEWS.some((v) => v === value);
}

/** Which quick view a row belongs to: the first step of a round is HR's layer, the later ones Leadership's. */
function inView(row: RequisitionRow, view: BoardView, mine: ReadonlyMap<string, ApprovalQueueItem>): boolean {
  const currentIndex = row.approvalTrack.findIndex((step) => step.isCurrent);
  switch (view) {
    case 'mine':
      return mine.has(row.id);
    case 'hr':
      return row.status === 'in_approval' && currentIndex === 0;
    case 'leadership':
      return row.status === 'in_approval' && currentIndex > 0;
    case 'approved':
      return row.status === 'approved' || row.status === 'converted';
    case 'returned':
      return row.status === 'rejected' || (row.status === 'draft' && row.currentVersion > 1);
    default:
      return true;
  }
}

function rangeOf(from: string | null, to: string | null): string | null {
  if (from && to && from !== to) return `${from} – ${to}`;
  return from ?? to;
}

/** "Checked by Miraj · waiting on leadership approval" — the track's words. */
function trackText(row: RequisitionRow, pending?: PendingState): string {
  if (pending) return PENDING_LABELS[pending];
  if (row.status === 'draft') return row.currentVersion > 1 ? 'Sent back to the requester' : 'Not sent yet';
  if (row.status === 'rejected') return 'Rejected';
  if (row.status === 'converted') return row.lastApprovedByName ? `Approved by ${row.lastApprovedByName} · job made` : 'Job made';
  if (row.status === 'approved') return row.lastApprovedByName ? `Approved by ${row.lastApprovedByName}` : 'Approved';
  const current = row.approvalTrack.find((step) => step.isCurrent);
  const checked = row.lastApprovedByName ? `Checked by ${row.lastApprovedByName} · ` : '';
  return current ? `${checked}waiting on ${current.name.toLowerCase()}` : REQUISITION_STATUS_LABELS[row.status];
}

/** Requested → each approval step → Job created, as segments: done, waiting, or not yet. */
function Track({ row, pending }: { readonly row: RequisitionRow; readonly pending?: PendingState }): ReactNode {
  const isSent = row.status !== 'draft';
  const isJobMade = row.status === 'converted';
  const isApproved = row.status === 'approved' || isJobMade;
  const segments: ('done' | 'wait' | 'off')[] = [
    isSent ? 'done' : 'off',
    ...row.approvalTrack.map((step): 'done' | 'wait' | 'off' =>
      step.status === 'approved' || isApproved ? 'done' : step.isCurrent ? 'wait' : 'off',
    ),
    isJobMade ? 'done' : 'off',
  ];
  return (
    <span className={styles.track}>
      <span className={styles.segments} aria-hidden>
        {segments.map((state, index) => (
          <i key={index} className={state === 'done' ? styles.segDone : state === 'wait' ? styles.segWait : styles.seg} />
        ))}
      </span>
      <span className={styles.trackText}>{trackText(row, pending)}</span>
    </span>
  );
}

function moneyOf(minor: string | null, currency: string | null): string | null {
  if (minor === null || !isCurrency(currency)) return null;
  return formatMoney({ amountMinor: BigInt(minor), currency });
}
